import React, { useCallback, useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { AdCampaign, CampaignStats, getCampaignStats } from '../../api/ads/ads.api';
import { formatCtr, isCampaignEnded } from '../../utils/ads-automation';
import AdRulesPanel from './ad-rules-panel';
import AdBudgetCreativePanel from './ad-budget-creative-panel';

type Tab = 'stats' | 'rules' | 'budget';
const TABS: { key: Tab; label: string }[] = [
  { key: 'stats', label: 'Thống kê' },
  { key: 'rules', label: 'Luật tự động' },
  { key: 'budget', label: 'Ngân sách & nội dung' },
];

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
);

const RANGES = [
  { days: 7, label: '7 ngày' },
  { days: 14, label: '14 ngày' },
  { days: 30, label: '30 ngày' },
];

const Metric: React.FC<{ label: string; value: string; sub?: string }> = ({
  label,
  value,
  sub,
}) => (
  <div className="rounded-lg ui-divider border p-3">
    <div className="text-xs ui-muted">{label}</div>
    <div className="text-lg font-bold ui-text">{value}</div>
    {sub ? <div className="text-[11px] ui-muted">{sub}</div> : null}
  </div>
);

const LoadFailed: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="h-40 grid place-items-center text-center">
    <div className="space-y-2">
      <p className="text-sm ui-muted">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-md ui-divider border text-sm px-4 py-1.5 ui-text"
      >
        Tải lại
      </button>
    </div>
  </div>
);

const AdStatsModal: React.FC<{
  campaign: AdCampaign;
  onClose: () => void;
  onChanged?: () => void;
}> = ({ campaign: initialCampaign, onClose, onChanged }) => {
  const [campaign, setCampaign] = useState<AdCampaign>(initialCampaign);
  const [tab, setTab] = useState<Tab>('stats');
  const [days, setDays] = useState(14);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    getCampaignStats(campaign.id, days)
      .then((s) => alive && setStats(s))
      .catch((e: any) => {
        if (alive) setLoadError(e?.response?.data?.message || 'Không tải được báo cáo. Bạn thử lại nhé.');
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [campaign.id, days, reloadKey]);

  const labels = (stats?.daily || []).map((d) => d.date.slice(5)); // MM-dd
  const gridColor = 'rgba(148,163,184,0.15)';
  const axis = { grid: { color: gridColor }, ticks: { font: { size: 10 } } };
  const commonOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { labels: { boxWidth: 12, font: { size: 11 } } } },
    scales: { x: axis, y: { ...axis, beginAtZero: true } },
  } as const;

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white dark:bg-dark2 p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold ui-text">Báo cáo hiệu suất</h3>
            <p className="text-xs ui-muted">{campaign.name}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/10">
            <X size={18} className="ui-text" />
          </button>
        </div>

        <div className="flex gap-4 border-b ui-divider">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`pb-2 text-sm font-semibold border-b-2 -mb-px ${
                tab === t.key ? 'border-[#0071A6] text-[#0071A6]' : 'border-transparent ui-muted'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'rules' && (
          <AdRulesPanel
            campaignId={campaign.id}
            hasDailyBudget={campaign.dailyBudgetCa != null}
            ended={isCampaignEnded(campaign.status)}
          />
        )}

        {tab === 'budget' &&
          (stats ? (
            <AdBudgetCreativePanel
              key={`${stats.pendingCreative?.id || 'none'}-${stats.pendingCreative?.reviewStatus || ''}`}
              campaign={campaign}
              pending={stats.pendingCreative}
              active={stats.activeCreative}
              onCampaignChange={(c) => {
                setCampaign(c);
                onChanged?.();
              }}
              onCreativeSent={() => {
                reload();
                onChanged?.();
              }}
            />
          ) : loadError && !loading ? (
            <LoadFailed message={loadError} onRetry={reload} />
          ) : (
            <div className="h-40 grid place-items-center">
              <Loader2 className="animate-spin ui-muted" />
            </div>
          ))}

        {tab === 'stats' && (
        <>
        {/* Chọn khoảng thời gian */}
        <div className="flex gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-1 w-fit">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                days === r.days
                  ? 'bg-white dark:bg-slate-700 text-primary shadow-sm'
                  : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {loadError && !loading ? (
          <LoadFailed message={loadError} onRetry={reload} />
        ) : loading || !stats ? (
          <div className="h-40 grid place-items-center">
            <Loader2 className="animate-spin ui-muted" />
          </div>
        ) : (
          <>
            {stats.fatigueDetectedAt && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 space-y-1">
                <div className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                  Quảng cáo đang nhàm
                </div>
                <p className="text-xs ui-text">
                  Tỉ lệ bấm giảm từ {formatCtr(stats.baselineCtr)} (3 ngày đầu) xuống{' '}
                  {formatCtr(stats.recentCtr)} (3 ngày gần nhất)
                  {stats.frequency != null
                    ? `. Mỗi người xem thấy quảng cáo trung bình ${stats.frequency.toLocaleString('vi-VN', { maximumFractionDigits: 1 })} lần trong 7 ngày`
                    : ''}
                  . Đổi nội dung mới thường kéo tỉ lệ bấm lên lại.
                </p>
                <button
                  type="button"
                  onClick={() => setTab('budget')}
                  className="rounded-md bg-amber-600 text-white text-xs px-3 py-1.5"
                >
                  Thay nội dung
                </button>
              </div>
            )}
            {/* Chỉ số tổng */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Metric label="Lượt hiển thị" value={stats.impressions.toLocaleString('vi-VN')} />
              <Metric label="Lượt bấm" value={stats.clicks.toLocaleString('vi-VN')} />
              <Metric label="Tỉ lệ bấm" value={formatCtr(stats.ctr)} sub="lượt bấm trên lượt hiển thị" />
              <Metric
                label="Đã tiêu"
                value={`${stats.spentCa.toLocaleString('vi-VN')} Ca`}
                sub={`còn ${stats.remainingCa.toLocaleString('vi-VN')} Ca`}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Metric label="Giá thực mỗi lượt bấm" value={`${stats.avgCpcCa.toLocaleString('vi-VN')} Ca`} />
              <Metric
                label="Giá thực mỗi 1.000 lượt hiển thị"
                value={`${stats.avgCpmCa.toLocaleString('vi-VN')} Ca`}
              />
            </div>
            {campaign.format === 'Video' && (stats.skips != null || stats.completes != null) ? (
              <div className="grid grid-cols-2 gap-2">
                <Metric
                  label="Bỏ qua quảng cáo"
                  value={(stats.skips ?? 0).toLocaleString('vi-VN')}
                  sub="lượt chèn trước video bị bỏ qua, không tính phí"
                />
                <Metric
                  label="Xem hết quảng cáo"
                  value={(stats.completes ?? 0).toLocaleString('vi-VN')}
                  sub="lượt chèn trước video chạy hết, không tính phí"
                />
              </div>
            ) : null}

            {/* Biểu đồ lượt hiển thị và lượt bấm theo ngày */}
            <div>
              <div className="text-sm font-semibold ui-text mb-1">Lượt hiển thị và lượt bấm theo ngày</div>
              <div className="h-48">
                <Line
                  data={{
                    labels,
                    datasets: [
                      {
                        label: 'Lượt hiển thị',
                        data: stats.daily.map((d) => d.impressions),
                        borderColor: '#0071A6',
                        backgroundColor: 'rgba(0,113,166,0.15)',
                        tension: 0.3,
                        fill: true,
                        pointRadius: 2,
                      },
                      {
                        label: 'Lượt bấm',
                        data: stats.daily.map((d) => d.clicks),
                        borderColor: '#F59E0B',
                        backgroundColor: 'rgba(245,158,11,0.15)',
                        tension: 0.3,
                        pointRadius: 2,
                      },
                    ],
                  }}
                  options={commonOpts}
                />
              </div>
            </div>

            {/* Biểu đồ chi tiêu theo ngày */}
            <div>
              <div className="text-sm font-semibold ui-text mb-1">Chi tiêu theo ngày (Ca)</div>
              <div className="h-40">
                <Bar
                  data={{
                    labels,
                    datasets: [
                      {
                        label: 'Ca tiêu',
                        data: stats.daily.map((d) => d.spentCa),
                        backgroundColor: '#0071A6',
                        borderRadius: 4,
                      },
                    ],
                  }}
                  options={commonOpts}
                />
              </div>
            </div>
          </>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default AdStatsModal;
